<template>
  <main ref="pageRef">
    <!-- ====== HERO ====== -->
    <section class="hero" ref="sectionRef" aria-label="Aumenta tus ventas">
      <div class="hero__bg">
        <!-- VIDEO DE FONDO -->
        <video
          ref="videoRef"
          class="hero__video"
          autoplay
          muted
          loop
          playsinline
          :poster="imgSrc || undefined"
        >
          <source src="@/assets/video.mp4" type="video/mp4" />
          Tu navegador no soporta el video de fondo.
        </video>

        <!-- Fallback opcional -->
        <img
          v-if="false && imgSrc"
          :src="imgSrc"
          :alt="imgAlt"
          class="hero__img"
          ref="imgRef"
        />

        <!-- Degradado oscuro (legibilidad) -->
        <div class="hero__overlay" aria-hidden="true"></div>
      </div>

      <div class="hero__content" ref="contentRef">
        <p class="hero__eyebrow" ref="eyebrowRef">Impulsa tu negocio con una Web + IA</p>

        <!-- TÍTULO en dos partes (para controlar el salto en móvil) -->
        <h1 class="hero__title" ref="titleRef">
          <span class="title-part1" ref="titlePart1">Sitios web personalizados</span>
          <span class="u-underline"></span>
          <span class="title-part2" ref="titlePart2">
            + IA que responde <span class="u-badge">24/7</span>
          </span>
        </h1>

        <p class="hero__subtitle" ref="subtitleRef">
          Somos latinos en USA y sabemos lo duro que es crecer. Por eso te armamos
          una web que atrae clientes y un <strong>asistente con IA</strong> que contesta
          por ti al instante (web/WhatsApp), enfocado en ventas.
        </p>

        <!-- CTAs -->
        <div class="hero__ctas" ref="ctasRef">
          <a :href="demoHref" target="_blank" rel="noopener" class="btn btn--primary">Agendar demo</a>
          <a :href="waHref" target="_blank" rel="noopener" class="btn btn--ghost">Escríbenos por WhatsApp</a>
        </div>

        <!-- Beneficios rápidos -->
        <ul class="hero__benefits" ref="benefitsRef">
          <li>✅ Más clientes reales</li>
          <li>⚡ Respuesta inmediata con IA</li>
          <li>📈 Listo para campañas y medición</li>
        </ul>

        <!-- Prueba social (mini) -->
        <div class="hero__trust" ref="trustRef">
          <span class="trust__kpi"><strong>+120</strong> sitios lanzados</span>
        </div>
      </div>
    </section>

    <!-- ====== DIVISOR (imagen entre secciones) ====== -->
    <section class="divider" ref="dividerRef" aria-hidden="true">
      <div class="divider__wrap">
        <img
          class="divider__img"
          :src="dividerSrcComputed"
          alt="Divisor decorativo"
          ref="dividerImgRef"
        />
      </div>
    </section>

    <!-- ====== SECCIÓN DE ABAJO ====== -->
    <section class="promo" ref="promoRef" aria-label="Cómo te ayudamos a vender más">
      <div class="promo__content">
        <p class="promo__line" ref="promoLineRef">
          Crezcamos tu negocio con
          <em class="promo__accent" ref="accentRef"> una Página web + Marketing con IA</em>
        </p>

        <p class="promo__note" ref="promoNoteRef">
          Landing enfocada en ventas, campañas medibles y un asistente que nunca dejará a tus clientes “en visto”.
          ¡Es lo que usamos nosotros mismos y realmente funciona aquí en USA!
        </p>

        <!-- Bullets de valor -->
        <ul class="promo__bullets" ref="bulletsRef">
          <li>Chat/WhatsApp con IA entrenada en tu negocio</li>
          <li>SEO técnico y performance (Core Web Vitals)</li>
        </ul>

        <div class="promo__ctas" ref="promoCtasRef">
          <a :href="demoHref" target="_blank" rel="noopener" class="btn btn--primary">Comencemos</a>
          <a :href="waHref" target="_blank" rel="noopener" class="btn btn--ghost">Quiero asesoría</a>
        </div>
      </div>
    </section>
  </main>
</template>

<script>
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
gsap.registerPlugin(ScrollTrigger)

export default {
  name: 'HeroMarketingIA',
  props: {
    imgSrc:   { type: String, default: '' },
    imgAlt:   { type: String, default: 'Fondo' },
    demoHref: { type: String, default: 'https://calendly.com/' },
    waPhone:  { type: String, default: '+16126005144' },
    waText:   { type: String, default: 'Hola, quiero información para vender más con web e IA.' },
    dividerSrc: { type: String, default: '' }
  },
  computed:{
    waHref(){
      const num  = this.waPhone.replace(/\D/g,'')
      const text = encodeURIComponent(this.waText)
      return `https://wa.me/${num}?text=${text}`
    },
    dividerSrcComputed(){
      return this.dividerSrc || new URL('@/assets/divider.jpg', import.meta.url).href
    }
  },
  mounted(){
    // Refs
    const section     = this.$refs.sectionRef
    const video       = this.$refs.videoRef
    const content     = this.$refs.contentRef
    const eyebrow     = this.$refs.eyebrowRef
    const titlePart1  = this.$refs.titlePart1
    const titlePart2  = this.$refs.titlePart2
    const subtitle    = this.$refs.subtitleRef
    const ctas        = this.$refs.ctasRef
    const benefits    = this.$refs.benefitsRef
    const trust       = this.$refs.trustRef

    const divider     = this.$refs.dividerRef
    const dividerImg  = this.$refs.dividerImgRef

    const promo       = this.$refs.promoRef
    const promoLine   = this.$refs.promoLineRef
    const accent      = this.$refs.accentRef
    const promoNote   = this.$refs.promoNoteRef
    const bullets     = this.$refs.bulletsRef
    const promoCtas   = this.$refs.promoCtasRef

    // Split por caracteres SOLO para la primera parte del título
    const original1 = titlePart1.textContent || ''
    titlePart1.textContent = ''
    const frag1 = document.createDocumentFragment()
    const chars = []
    for (const ch of original1) {
      const span = document.createElement('span')
      span.className = 'char'
      span.textContent = ch === ' ' ? '\u00A0' : ch
      frag1.appendChild(span); chars.push(span)
    }
    titlePart1.appendChild(frag1)

    // Estados iniciales
    gsap.set(content,    { opacity: 0, y: 10 })
    gsap.set(eyebrow,    { opacity: 0, y: -8, letterSpacing: '3px' })
    gsap.set(chars,      { opacity: 0, y: 16 })
    gsap.set(titlePart2, { opacity: 0, y: 10 })
    gsap.set(subtitle,   { opacity: 0, y: 10 })
    gsap.set(ctas,       { opacity: 0, y: 12 })
    gsap.set(benefits,   { opacity: 0, y: 12 })
    gsap.set(trust,      { opacity: 0, y: 12 })

    gsap.set(dividerImg, { opacity: 0, y: 30, scale: 0.96, rotate: -2 })

    gsap.set(promoLine,  { opacity: 0, y: 16 })
    gsap.set(accent,     { color: '#18a7ff', fontStyle: 'italic' })
    gsap.set(promoNote,  { opacity: 0, y: 12 })
    gsap.set(bullets,    { opacity: 0, y: 12 })
    gsap.set(promoCtas,  { opacity: 0, y: 12 })
    document.documentElement.style.setProperty('--accent-underline', '0%')

    // Video (Ken Burns ligero)
    if (video) {
      gsap.fromTo(video, { scale: 1.06, filter: 'brightness(0.95) contrast(1.03)' }, {
        scale: 1,
        filter: 'brightness(1) contrast(1)',
        duration: 1.2,
        ease: 'power2.out',
        scrollTrigger: { trigger: section, start: 'top 85%', once: true }
      })
    }

    // Timeline 1: HERO
    const tlHero = gsap.timeline({
      scrollTrigger: { trigger: section, start: 'top 85%', once: true }
    })
    tlHero
      .to(content,    { opacity: 1, y:0, duration: 0.4,  ease: 'power2.out' }, 0.05)
      .to(eyebrow,    { opacity: 1, y:0, letterSpacing: '1px', duration: 0.45, ease: 'power2.out' }, '>-0.2')
      .to(chars,      { opacity: 1, y:0, duration: 0.02, stagger:{ each:0.015 }, ease:'power1.out' }, '>-0.2')
      .to(titlePart2, { opacity: 1, y:0, duration: 0.35, ease:'power2.out' }, '>-0.15')
      .to(subtitle,   { opacity: 1, y:0, duration: 0.45, ease:'power2.out' }, '>-0.05')
      .to(ctas,       { opacity: 1, y:0, duration: 0.5,  ease: 'back.out(1.6)' }, '>-0.05')
      .to(benefits,   { opacity: 1, y:0, duration: 0.5,  ease: 'power2.out' }, '>-0.2')
      .to(trust,      { opacity: 1, y:0, duration: 0.5,  ease: 'power2.out' }, '>-0.25')

    // Parallax suave del video
    ScrollTrigger.create({
      trigger: section,
      start: 'top bottom',
      end: 'bottom top',
      scrub: 0.6,
      onUpdate: self => {
        gsap.to(video, { y: self.progress * 40, overwrite: 'auto', duration: 0.1 })
      }
    })

    // Timeline 2: DIVIDER
    const tlDivider = gsap.timeline({
      scrollTrigger: { trigger: divider, start: 'top 85%', once: true }
    })
    tlDivider
      .to(dividerImg, {
        opacity: 1, y: 0, scale: 1, rotate: 0,
        duration: 0.8, ease: 'power3.out'
      })
      .to(dividerImg, {
        filter: 'drop-shadow(0 8px 20px rgba(0,0,0,0.35))',
        duration: 0.6, ease: 'power2.out'
      }, '-=0.3')

    // Parallax divisor
    gsap.to(dividerImg, {
      y: -20,
      ease: 'none',
      scrollTrigger: { trigger: divider, start: 'top bottom', end: 'bottom top', scrub: 0.5 }
    })

    // Timeline 3: PROMO
    const tlPromo = gsap.timeline({
      scrollTrigger: { trigger: promo, start: 'top 85%', once: true }
    })
    tlPromo
      .to(promoLine, { opacity: 1, y: 0, duration: 0.55, ease: 'power2.out' }, 0)
      .to(accent, {
        '--ux': '100%',
        duration: 0.7,
        ease: 'power2.out',
        onUpdate: () => {
          const p = gsap.getProperty(accent, '--ux')
          accent.style.setProperty('--accent-underline', p + '%')
        }
      }, '<0.05')
      .to(promoNote, { opacity: 1, y: 0, duration: 0.45, ease: 'power2.out' }, '>-0.2')
      .to(bullets,   { opacity: 1, y: 0, duration: 0.45, ease: 'power2.out' }, '>-0.1')
      .to(promoCtas, { opacity: 1, y: 0, duration: 0.55, ease: 'back.out(1.6)' }, '>-0.05')
  }
}
</script>

<style scoped>
/* ====== HERO con VIDEO ====== */
.hero{
  position: relative;
  width: 100%;
  min-height: 86vh;
  background: #000;
  display: grid;
  place-items: center;
  overflow: hidden;
}
.hero__bg{ position:absolute; inset:0; overflow:hidden; }
.hero__video{
  position:absolute; inset:0; width:100%; height:100%;
  object-fit:cover; object-position:center; transform: translateZ(0);
}
.hero__img{ display:none; }
.hero__overlay{
  position:absolute; inset:0;
  background:
    radial-gradient(120% 90% at 50% 10%, rgba(0,0,0,0) 40%, rgba(0,0,0,0.55) 100%),
    linear-gradient( to bottom, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.35) 30%, rgba(0,0,0,0.65) 70%, rgba(0,0,0,0.80) 100% );
  pointer-events: none;
}

.hero__content{
  position: relative; z-index: 2; text-align: center; padding: 24px;
  width: min(1100px, 92vw); color: #fff;
  margin-top: 150px;
}

/* Eyebrow */
.hero__eyebrow{
  margin: 0 0 10px; font-family: 'Montserrat', system-ui, sans-serif;
  font-weight: 700; font-size: 14px; color: #a7a7a7; letter-spacing: 1px; text-transform: uppercase;
}

/* Título */
.hero__title{
  margin: 0 auto 8px; text-align: center;
  font-family: 'Montserrat', system-ui, sans-serif; font-weight: 900;
  line-height: 1.14; font-size: 40px; color: #ffffff; display:block; width:100%;
  text-wrap: balance;
}

/* Subrayado */
.u-underline{ position: relative; display: inline-block; }
.u-underline::after{
  content:""; position:absolute; left:0; bottom:-6px; width:100%; height:3px; background:#7a2bff; border-radius:2px;
}

/* Badge */
.u-badge{
  background:#10b981; color:#07130f; border-radius:8px; padding:2px 8px; font-size:16px; font-weight:800;
  position: relative; top:-2px;
}

/* Texto */
.hero__subtitle{ margin: 12px 0 18px; font-family:'Montserrat', system-ui, sans-serif;
  font-weight:500; color:#dcdcdc; font-size:18px; }

/* CTAs */
.hero__ctas{ display:inline-flex; gap:12px; align-items:center; justify-content:center; margin-bottom:12px; }
.btn{ appearance:none; border:0; border-radius:10px; padding:12px 20px;
  font-family:'Montserrat', system-ui, sans-serif; font-weight:700; font-size:16px; cursor:pointer; text-decoration:none; display:inline-block; }
.btn--primary{ color:#fff; background: linear-gradient(180deg, #7a2bff, #5720c9); box-shadow:0 10px 18px rgba(87,32,201,.35); }
.btn--ghost{ color:#fff; background:transparent; border:1px solid #2d2d2d; box-shadow:0 6px 16px rgba(0,0,0,.25); }

/* Bullets + trust */
.hero__benefits{ list-style:none; padding:0; margin:8px 0 10px; display:flex; gap:18px; flex-wrap:wrap; justify-content:center; color:#d7d7d7; font-family:'Montserrat', system-ui, sans-serif; font-size:14px; font-weight:600; }
.hero__trust{ margin-top:4px; display:flex; gap:18px; flex-wrap:wrap; justify-content:center; color:#bdbdbd; font-family:'Montserrat', system-ui, sans-serif; font-size:13px; }
.trust__kpi strong{ color:#fff; }

/* ====== DIVIDER ====== */
.divider{ background:#0b0b0b; display:grid; place-items:center; }
.divider__wrap{ width:100%; display:grid; place-items:center; }
.divider__img{ width:100%; height:auto; display:block; filter: drop-shadow(0 4px 14px rgba(0,0,0,0.35)); }

/* ====== PROMO ====== */
.promo{ background:#111; color:#fff; padding:28px 16px 36px; display:grid; place-items:center; }
.promo__content{ width:min(1100px, 92vw); text-align:center; }
.promo__line{ margin:0 0 8px; font-family:'Montserrat', system-ui, sans-serif; font-weight:800; color:#fff; font-size:22px; }
.promo__accent{ font-style:italic; color:#18a7ff; position:relative; }
.promo__accent::after{ content:""; position:absolute; left:0; bottom:-4px; height:2px; width:var(--accent-underline, 0%); background:currentColor; transition:width .2s linear; }
.promo__note{ margin:6px 0 16px; font-family:'Montserrat', system-ui, sans-serif; font-weight:500; color:#cfcfcf; font-size:16px; }
.promo__bullets{ list-style:none; padding:0; margin:8px 0 18px; display:flex; gap:18px; flex-wrap:wrap; justify-content:center; color:#d7d7d7; font-family:'Montserrat', system-ui, sans-serif; font-size:14px; font-weight:600; }
.promo__ctas{ display:inline-flex; gap:12px; align-items:center; justify-content:center; }

/* ====== BREAKPOINTS ====== */
@media (min-width:1400px){
  .hero__title{ font-size:46px; }
  .promo__line{ font-size:24px; }
}
@media (max-width:1024px){
  .hero__title{ font-size:32px; line-height:1.18; }
  .promo__line{ font-size:20px; }
}
@media (max-width:768px){
  .hero{ min-height:70vh; }
  .hero__content{ margin-top:110px; }
  .hero__title{ font-size:26px; line-height:1.22; }
  /* En tablet/móvil: forzamos que cada parte sea un renglón */
  .hero__title .title-part1,
  .hero__title .title-part2{ display:block; }
  .hero__title .title-part2{ margin-top:4px; }
  .promo__line{ font-size:20px; }
}
@media (max-width:480px){
  .hero{ min-height:64vh; }
  .hero__content{ margin-top:95px; }
  .hero__title{ font-size:20px; line-height:1.28; }
  .hero__subtitle{ font-size:14px; }
  .promo__line{ font-size:18px; }
  .promo__note{ font-size:14px; }
  .u-badge{ font-size:14px; }
}

.char{ display:inline-block; }
</style>
